<script setup lang="ts">
// トーンカーブ編集: 点のドラッグ / クリックで追加 / ダブルクリックで削除。
import { computed, ref, watch } from 'vue'
import { curveLut } from '@/render/adjust/lut'
import type { CurvePoints } from '@/types/document'

const props = defineProps<{ points: CurvePoints }>()
const emit = defineEmits<{ input: [pts: CurvePoints]; change: [pts: CurvePoints] }>()
const SIZE = 200
const local = ref<CurvePoints>(props.points.map(p => [p[0], p[1]]))
watch(() => props.points, p => { if (!dragging.value) local.value = p.map(q => [q[0], q[1]]) })
const lut = computed(() => curveLut(local.value))
const path = computed(() => {
  let d = ''
  for (let x = 0; x < 256; x++) {
    const sx = (x / 255) * SIZE, sy = SIZE - (lut.value[x]! / 255) * SIZE
    d += (x === 0 ? 'M' : 'L') + sx.toFixed(1) + ' ' + sy.toFixed(1) + ' '
  }
  return d
})
const dragging = ref<number | null>(null)
const svg = ref<SVGSVGElement | null>(null)
function toVal(e: PointerEvent): [number, number] {
  const r = svg.value!.getBoundingClientRect()
  const x = Math.max(0, Math.min(255, ((e.clientX - r.left) / r.width) * 255))
  const y = Math.max(0, Math.min(255, 255 - ((e.clientY - r.top) / r.height) * 255))
  return [Math.round(x), Math.round(y)]
}
function onDown(e: PointerEvent) {
  const [x, y] = toVal(e)
  const hit = local.value.findIndex(p => Math.abs(p[0] - x) <= 8 && Math.abs(p[1] - y) <= 8)
  if (hit >= 0) dragging.value = hit
  else {
    const added: [number, number] = [x, y]
    local.value = [...local.value, added].sort((a, b) => a[0]! - b[0]!) as CurvePoints
    dragging.value = local.value.findIndex(p => p[0] === x && p[1] === y)
  }
  ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
  emit('input', local.value)
}
function onMove(e: PointerEvent) {
  if (dragging.value === null) return
  const [x, y] = toVal(e)
  const i = dragging.value
  const prevX = i > 0 ? local.value[i - 1]![0] : 0
  const nextX = i < local.value.length - 1 ? local.value[i + 1]![0] : 255
  const nx = i === 0 ? 0 : i === local.value.length - 1 ? 255 : Math.max(prevX + 1, Math.min(nextX - 1, x))
  local.value = local.value.map((p, k): [number, number] => (k === i ? [nx, y] : [p[0], p[1]]))
  emit('input', local.value)
}
function onUp() {
  if (dragging.value === null) return
  dragging.value = null
  emit('change', local.value)
}
function onDblClick(e: MouseEvent) {
  const r = svg.value!.getBoundingClientRect()
  const x = ((e.clientX - r.left) / r.width) * 255, y = 255 - ((e.clientY - r.top) / r.height) * 255
  const hit = local.value.findIndex(p => Math.abs(p[0] - x) <= 8 && Math.abs(p[1] - y) <= 8)
  if (hit > 0 && hit < local.value.length - 1) {
    local.value = local.value.filter((_, k) => k !== hit)
    emit('change', local.value)
  }
}
</script>

<template>
  <svg ref="svg" class="curve" :viewBox="`0 0 ${SIZE} ${SIZE}`" @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp" @pointercancel="onUp" @dblclick="onDblClick">
    <rect x="0" y="0" :width="SIZE" :height="SIZE" class="bg" />
    <line v-for="i in 3" :key="'v' + i" :x1="(SIZE * i) / 4" y1="0" :x2="(SIZE * i) / 4" :y2="SIZE" class="grid" />
    <line v-for="i in 3" :key="'h' + i" x1="0" :y1="(SIZE * i) / 4" :x2="SIZE" :y2="(SIZE * i) / 4" class="grid" />
    <line x1="0" :y1="SIZE" :x2="SIZE" y2="0" class="diag" />
    <path :d="path" class="line" />
    <circle v-for="(p, i) in local" :key="i" :cx="(p[0] / 255) * SIZE" :cy="SIZE - (p[1] / 255) * SIZE" r="4" class="pt" />
  </svg>
</template>

<style scoped>
.curve {
  width: 100%;
  aspect-ratio: 1;
  touch-action: none;
  cursor: crosshair;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
}
.bg {
  fill: var(--bg-0);
}
.grid {
  stroke: var(--line);
  stroke-width: 1;
}
.diag {
  stroke: var(--fg-3);
  stroke-dasharray: 3 3;
}
.line {
  fill: none;
  stroke: var(--fg-0);
  stroke-width: 1.5;
}
.pt {
  fill: var(--accent);
  stroke: #fff;
  stroke-width: 1;
}
</style>
